import {
  Enter,
  ItemsPerPage,
  of,
  Results,
  Search,
  Showing,
  to,
} from "Components/constants/Common";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ButtonGroup,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";

interface PaginationProps {
  children?: React.ReactNode;
  totalRecords: number;
  totalPages: number;
  data?: Array<any>;
  pageSizeValue: number;
  setPageSizeValue: (item: number) => void;
  setOnValueSearchBar: (item: string) => void;
  setCurrentPageValue: (item: number) => void;
}

interface DebouncedInputProps {
  value: string;
  onChange: (value: string) => void;
  debounce?: number;
  placeholder?: string;
  [key: string]: any;
}

const Pagination: React.FC<PaginationProps> = ({
  children,
  data,
  totalRecords,
  totalPages,
  pageSizeValue,
  setPageSizeValue,
  setOnValueSearchBar,
  setCurrentPageValue,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(pageSizeValue);
  const [pageSizesArray] = useState([12, 24, 36, 48, 60]);
  const [onValueSearch, setOnValueSearch] = useState<string>("");

  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalRecords);

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setPageSizeValue(value);
    setCurrentPage(1);
    setCurrentPageValue(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === Enter) {
      e.preventDefault();
    }
  };

  const handleValueChange = (newValue: string) => {
    setOnValueSearch(newValue);
    setOnValueSearchBar(newValue);
    if (newValue !== "") {
      setCurrentPage(1);
      setCurrentPageValue(1);
    }
  };

  const handlePreviousPage = () => {
    setCurrentPage(currentPage - 1);
    setCurrentPageValue(currentPage - 1);
  };

  const handleNextPage = () => {
    setCurrentPage(currentPage + 1);
    setCurrentPageValue(currentPage + 1);
  };

  const renderPageNumbers = () => {
    const MAX_PAGES_DISPLAYED = pageSize;
    const totalPages11 = totalPages;
    const currentPage11 = currentPage;

    let startPage = Math.max(1, currentPage11 - 2);
    let endPage = Math.min(totalPages11, startPage + MAX_PAGES_DISPLAYED - 1);

    if (endPage - startPage + 1 < MAX_PAGES_DISPLAYED) {
      startPage = Math.max(1, endPage - MAX_PAGES_DISPLAYED + 1);
    }

    const pageNumbers = [];

    if (startPage > 1) {
      pageNumbers.push(
        <li key="ellipsisStart" className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li
          key={i}
          className={currentPage11 === i ? "page-item active" : "page-item"}
        >
          <Link
            className="page-link page-success"
            onClick={(e) => {
              e.preventDefault();
              setCurrentPage(i);
              setCurrentPageValue(i);
            }}
            to={""}
          >
            {i}
          </Link>
        </li>
      );
    }

    if (endPage < totalPages11) {
      pageNumbers.push(
        <li key="ellipsisEnd" className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      );
    }

    return pageNumbers;
  };

  const DebouncedInput: React.FC<DebouncedInputProps> = ({
    value: initialValue,
    onChange,
    debounce = 500,
    ...props
  }) => {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
      const timeout = setTimeout(() => {
        onChange(value);
      }, debounce);

      return () => clearTimeout(timeout);
    }, [debounce, onChange, value]);

    return (
      <input
        {...props}
        value={value}
        id="search-bar-0"
        className="form-control shadow-none search"
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
      />
    );
  };

  return (
    <>
      <div>
        <Row className="mb-1">
          <form>
            <Row className="d-flex gap-2 justify-content-between align-items-center">
              <Col sm={4} className="text-start pagenumber">
                {ItemsPerPage}: &nbsp;
                <ButtonGroup>
                  <UncontrolledDropdown>
                    <DropdownToggle
                      tag="button"
                      className="btn btn-link border px-2 py-1 shadow-none text-dark"
                      type="button"
                    >
                      {pageSize} <i className="mdi mdi-chevron-down ms-1"></i>
                    </DropdownToggle>
                    <DropdownMenu>
                      {pageSizesArray.map((size) => (
                        <DropdownItem
                          key={size}
                          onClick={() => handlePageSizeChange(size)}
                        >
                          {size}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </ButtonGroup>
              </Col>

              <Col
                sm={4}
                className="d-flex align-items-end justify-content-end mt-3"
              >
                <div className={"search-box me-2 mb-2 d-inline-block"}>
                  <DebouncedInput
                    value={onValueSearch ?? ""}
                    onChange={(value) => handleValueChange(value)}
                    placeholder={Search}
                  />
                  <i className="bx bx-search-alt position-absolute top-50 start-0 translate-middle-y ps-3"></i>
                </div>
              </Col>
            </Row>
          </form>
        </Row>
      </div>
      <div>{children}</div>
      <div>
        {data?.length !== 0 && (
          <Row className="align-items-center mt-2 g-3 text-center text-sm-start pagenumber">
            <div className="col-sm">
              <div className="text-muted">
                {Showing}{" "}
                <span className="fw-semibold ms-1">
                  {startIndex} {to} {endIndex}
                </span>{" "}
                {of} <span className="fw-semibold">{totalRecords}</span>{" "}
                {Results}
              </div>
            </div>
            <div className="col-sm-auto">
              <ul className="pagination pagination-separated pagination-sm justify-content-center align-items-center justify-content-sm-start mb-0">
                <li
                  className={
                    currentPage === 1 ? "page-item disabled" : "page-item"
                  }
                >
                  <Link
                    className="page-link p-0 cursor-pointer"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePreviousPage();
                    }}
                  >
                    <i className="mdi mdi-chevron-left fs-4 m-r-5"></i>
                  </Link>
                </li>
                {renderPageNumbers()}
                <li
                  className={
                    currentPage === totalPages
                      ? "page-item disabled"
                      : "page-item"
                  }
                >
                  <Link
                    className="page-link p-0 cursor-pointer"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNextPage();
                    }}
                  >
                    <i className="mdi mdi-chevron-right fs-4 m-r-5"></i>
                  </Link>
                </li>
              </ul>
            </div>
          </Row>
        )}
      </div>
    </>
  );
};

export default Pagination;
